'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import type { Hex, Address as ViemAddress } from 'viem';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { Skeleton } from '@repo/ui';
import { useLayoutEffect, useRef, useState } from 'react';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import type { VaultV2ExposureSectionData } from '../../VaultV2Overview/VaultV2Exposure/hooks/useVaultV2ExposureData';
import { VaultV2RowList, VaultV2RowListRow } from '../../components/VaultV2RowList';
import { formatPenalty, type PublicAllocatorMarketKey } from './publicAllocator';
import PublicAllocatorTable from './PublicAllocatorTable';
import { usePublicAllocatorSettings } from './usePublicAllocatorSettings';
export default function PublicAllocatorSection(t0) {
  const $ = _c(42);
  const {
    address,
    chainId,
    data
  } = t0;
  let t1;
  if ($[0] !== data.exposure.markets) {
    t1 = data.exposure.markets.map(_temp);
    $[0] = data.exposure.markets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const markets = t1;
  let t2;
  if ($[2] !== address || $[3] !== chainId || $[4] !== markets) {
    t2 = {
      chainId,
      vaultAddress: address,
      markets
    };
    $[2] = address;
    $[3] = chainId;
    $[4] = markets;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    rows,
    isLoading,
    isError,
    isUnsupported,
    isSetUp,
    canPullFromIdle,
    penaltyWad
  } = usePublicAllocatorSettings(t2);
  const sectionRef = useRef(null);
  const [loadingHeight, setLoadingHeight] = useState(null);
  let t3;
  let t4;
  if ($[6] !== isLoading) {
    t3 = () => {
      if (isLoading && sectionRef.current) {
        setLoadingHeight(sectionRef.current.getBoundingClientRect().height);
      }
    };
    t4 = [isLoading];
    $[6] = isLoading;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useLayoutEffect(t3, t4);
  let t5;
  if ($[9] !== loadingHeight) {
    t5 = loadingHeight === null ? {
      scrollAreaStyle: undefined,
      className: undefined,
      height: "250px"
    } : {
      scrollAreaStyle: {
        height: loadingHeight
      },
      className: "absolute inset-0",
      height: "auto"
    };
    $[9] = loadingHeight;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const stateCard = t5;
  if (isError) {
    const t6 = loadingHeight === null ? "250px" : 0;
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        borderRadius: "0"
      };
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== stateCard.className || $[13] !== t6) {
      t8 = <ErrorDataUnavailable minHeight={t6} className={stateCard.className} style={t7} />;
      $[12] = stateCard.className;
      $[13] = t6;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== stateCard.scrollAreaStyle || $[16] !== t8) {
      t9 = <TableScrollArea className="relative grid h-full w-full rounded-[20px]" style={stateCard.scrollAreaStyle}>{t8}</TableScrollArea>;
      $[15] = stateCard.scrollAreaStyle;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  if (!isLoading && (isUnsupported || !isSetUp)) {
    const chainName = InterfaceChainUtils.tryGetChain(chainId)?.name ?? "this network";
    const t6 = isUnsupported ? `Public Allocator isn't available on ${chainName}` : "Public Allocator isn't set up for this Vault";
    let t7;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        borderRadius: "0"
      };
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== stateCard.className || $[20] !== stateCard.height || $[21] !== t6) {
      t8 = <TableNoResults text={t6} height={stateCard.height} className={stateCard.className} style={t7} />;
      $[19] = stateCard.className;
      $[20] = stateCard.height;
      $[21] = t6;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== stateCard.scrollAreaStyle || $[24] !== t8) {
      t9 = <TableScrollArea className="relative grid h-full w-full rounded-[20px]" style={stateCard.scrollAreaStyle}>{t8}</TableScrollArea>;
      $[23] = stateCard.scrollAreaStyle;
      $[24] = t8;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    return t9;
  }
  let t6;
  if ($[26] !== penaltyWad) {
    t6 = penaltyWad === null ? null : formatPenalty(penaltyWad);
    $[26] = penaltyWad;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const penalty = t6;
  let t7;
  if ($[28] !== canPullFromIdle || $[29] !== chainId || $[30] !== data || $[31] !== isLoading || $[32] !== markets || $[33] !== rows) {
    t7 = <PublicAllocatorTable isLoading={isLoading} markets={markets} data={data} chainId={chainId} rows={rows} canPullFromIdle={canPullFromIdle} />;
    $[28] = canPullFromIdle;
    $[29] = chainId;
    $[30] = data;
    $[31] = isLoading;
    $[32] = markets;
    $[33] = rows;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== isLoading || $[36] !== penalty) {
    t8 = <VaultV2RowList><VaultV2RowListRow label="Penalty" description="Paid by whoever reallocates, as a % of the amount moved. Goes to depositors." valueClassName="text-xs tabular-nums">{isLoading ? <Skeleton className="h-4 w-16" /> : <span>{penalty?.isBelowThreshold ? penalty.exact : penalty?.display ?? "Unavailable"}</span>}</VaultV2RowListRow></VaultV2RowList>;
    $[35] = isLoading;
    $[36] = penalty;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  let t9;
  if ($[38] !== isLoading || $[39] !== t7 || $[40] !== t8) {
    t9 = <div ref={sectionRef} aria-label="Public Allocator settings" aria-busy={isLoading} className="gap-lg flex min-w-0 flex-col">{t7}{t8}</div>;
    $[38] = isLoading;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  return t9;
}
function _temp(market) {
  return {
    marketId: market.market.marketId as Hex,
    adapter: market.adapterAddress as ViemAddress
  };
}